import React from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Grid,
  Paper,
  Typography,
  Collapse,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
  AllInclusive,
} from '@mui/icons-material';

// Store Zustand et hooks React Query
import { useGalleryStore } from '../stores/useGalleryStore';
import { useCategories } from '../hooks/useTableauxQueries';
import { getViewsStatistics } from '../services/viewsService';

export default function SearchFilters({ tableaux = [] }) {
  const {
    filters,
    setFilters,
    resetFilters, // Corriger le nom de la fonction
  } = useGalleryStore();

  // Utiliser React Query pour les catégories
  const { data: categories = [] } = useCategories();

  const [isAdvancedOpen, setIsAdvancedOpen] = React.useState(false);
  const [dbStats, setDbStats] = React.useState(null);

  // Récupérer les statistiques depuis la base de données
  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        console.log('🔄 Récupération des stats depuis la BD...');
        const result = await getViewsStatistics();
        if (result.success) {
          console.log('📊 Stats BD récupérées:', result.data);
          setDbStats(result.data);
        }
      } catch (error) {
        console.error('❌ Erreur récupération stats BD:', error);
      }
    };

    fetchStats();
    // Refresh des stats toutes les 30 secondes
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  // Calculer les stats avec données BD + fallback local
  const stats = React.useMemo(() => {
    console.log(
      '🔄 Recalcul des stats - Source BD:',
      !!dbStats,
      'Tableaux locaux:',
      tableaux?.length || 0
    );

    // Si on a les stats de la BD, les utiliser
    if (dbStats && dbStats.avg_by_status) {
      const disponibles =
        dbStats.avg_by_status.find((s) => s.disponibilite === 'disponible')?.count || 0;
      const vendus = dbStats.avg_by_status.find((s) => s.disponibilite === 'vendu')?.count || 0;
      const reserves = dbStats.avg_by_status.find((s) => s.disponibilite === 'réservé')?.count || 0;
      const total = disponibles + vendus + reserves;

      // Calculer les prix depuis les données locales (car pas dans les stats BD)
      const prices = tableaux
        .map((t) => parseFloat(t.prix?.toString().replace('€', '').replace(',', '.') || 0))
        .filter((p) => p > 0);

      const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
      const maxPrice = prices.length > 0 ? Math.max(...prices) : 1000;

      const calculatedStats = {
        total,
        disponibles,
        vendus,
        reserves,
        priceRange: [minPrice, maxPrice],
        totalViews: dbStats.total_views || 0,
        source: 'database',
      };

      console.log('📊 Stats calculées depuis BD:', calculatedStats);
      return calculatedStats;
    }

    // Fallback: calculer depuis les données locales
    if (!tableaux || tableaux.length === 0) {
      return {
        total: 0,
        disponibles: 0,
        vendus: 0,
        reserves: 0,
        priceRange: [0, 0],
        totalViews: 0,
        source: 'fallback',
      };
    }

    const disponibles = tableaux.filter((t) => t.disponibilite === 'disponible').length;
    const vendus = tableaux.filter((t) => t.disponibilite === 'vendu').length;
    const reserves = tableaux.filter((t) => t.disponibilite === 'réservé').length;

    const prices = tableaux
      .map((t) => parseFloat(t.prix?.toString().replace('€', '').replace(',', '.') || 0))
      .filter((p) => p > 0);

    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
    const maxPrice = prices.length > 0 ? Math.max(...prices) : 1000;

    const calculatedStats = {
      total: tableaux.length,
      disponibles,
      vendus,
      reserves,
      priceRange: [minPrice, maxPrice],
      totalViews: tableaux.reduce((sum, t) => sum + (t.vues || 0), 0),
      source: 'fallback',
    };

    console.log('📊 Stats calculées (fallback local):', calculatedStats);
    return calculatedStats;
  }, [tableaux, tableaux?.length, dbStats]);

  // Refresh périodique des stats (toutes les 2 minutes)
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey((prev) => prev + 1);
    }, 120000);

    return () => clearInterval(interval);
  }, []);

  // Recalculer les stats quand refreshKey change
  React.useEffect(() => {
    if (refreshKey > 0) {
      const fetchStats = async () => {
        try {
          const result = await getViewsStatistics();
          if (result.success) {
            setDbStats(result.data);
          }
        } catch (error) {
          console.error('❌ Erreur refresh stats BD:', error);
        }
      };
      fetchStats();
    }
  }, [refreshKey]);

  // Gestionnaires d'événements optimisés
  const handleSearchChange = (value) => {
    setFilters({ search: value });
  };

  const handleCategoryChange = (value) => {
    setFilters({ category: value });
  };

  const handleDisponibiliteChange = (value) => {
    setFilters({ disponibilite: value });
  };

  const handleClearAll = () => {
    resetFilters(); // Utiliser resetFilters au lieu de clearFilters
    setIsAdvancedOpen(false);
  };

  // Remplacer le slider par deux champs numériques pour min et max
  const [isPriceFilterEnabled, setIsPriceFilterEnabled] = React.useState(false);
  const [priceMin, setPriceMin] = React.useState(0);
  const [priceMax, setPriceMax] = React.useState(1000000);
  const handlePriceMinChange = (e) => {
    const value = Number(e.target.value);
    setPriceMin(value);
    setFilters({ priceRange: [value, priceMax] });
  };
  const handlePriceMaxChange = (e) => {
    const value = Number(e.target.value);
    setPriceMax(value);
    setFilters({ priceRange: [priceMin, value] });
  };
  const handlePriceFilterToggle = (enabled) => {
    setIsPriceFilterEnabled(enabled);
    if (!enabled) {
      setFilters({ priceRange: [0, 1000000] });
      setPriceMin(0);
      setPriceMax(1000000);
    }
  };

  // Compter les filtres actifs
  const activeFiltersCount = Object.values(filters).filter((value) => {
    if (Array.isArray(value)) {
      return false;
    }
    return value !== '' && value !== null && value !== undefined;
  }).length;

  // Au montage, enlever le filtre de base pour afficher tous les tableaux
  React.useEffect(() => {
    setFilters({ search: '', category: '', disponibilite: '', priceRange: [0, 1000000] });
  }, []);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Search color="primary" />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Recherche et Filtres
        </Typography>
        <Chip label={`${stats.total} œuvres`} color="primary" variant="outlined" size="small" />
        {activeFiltersCount > 0 && (
          <Chip
            label={`${activeFiltersCount} filtre${activeFiltersCount > 1 ? 's' : ''}`}
            color="secondary"
            size="small"
            onDelete={handleClearAll}
            deleteIcon={<Clear aria-label="Effacer tous les filtres" />}
          />
        )}
      </Box>

      {/* Recherche principale */}
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            placeholder="Rechercher par titre, artiste, description..."
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search sx={{ mr: 1, color: 'text.secondary' }} aria-label="Rechercher" />
              ),
            }}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel shrink>Catégorie</InputLabel>
            <Select
              value={filters.category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              label="Catégorie"
              notched
              sx={{
                minHeight: 64,
                '& .MuiSelect-select': {
                  minHeight: '24px !important',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 14px !important',
                  paddingRight: '40px !important', // Espace pour la flèche
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '& .MuiSelect-icon': {
                  right: '8px', // Position fixe de la flèche
                },
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minHeight: 24,
                        width: 'calc(100% - 32px)', // Largeur réduite pour éviter la flèche
                        overflow: 'hidden',
                      }}
                    >
                      <AllInclusive color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        Toutes les catégories
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minHeight: 24,
                      width: 'calc(100% - 32px)', // Largeur réduite pour éviter la flèche
                      overflow: 'hidden',
                    }}
                  >
                    <Typography variant="body1" fontWeight={600} noWrap>
                      {selected}
                    </Typography>
                  </Box>
                );
              }}
            >
              <MenuItem value="">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 40,
                    width: '100%',
                  }}
                >
                  <AllInclusive color="primary" />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      Toutes les catégories
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Voir toutes les œuvres
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.nom}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      minHeight: 40,
                      justifyContent: 'center',
                      width: '100%',
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {cat.nom}
                    </Typography>
                    {cat.description && (
                      <Typography variant="caption" color="text.secondary">
                        {cat.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel shrink>Disponibilité</InputLabel>
            <Select
              value={filters.disponibilite}
              onChange={(e) => handleDisponibiliteChange(e.target.value)}
              label="Disponibilité"
              notched
              sx={{
                minHeight: 64,
                '& .MuiSelect-select': {
                  minHeight: '24px !important',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 14px !important',
                  paddingRight: '40px !important', // Espace pour la flèche
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '& .MuiSelect-icon': {
                  right: '8px', // Position fixe de la flèche
                },
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        minHeight: 24,
                        width: 'calc(100% - 32px)', // Largeur réduite pour éviter la flèche
                        overflow: 'hidden',
                      }}
                    >
                      <AllInclusive color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        Tous les statuts
                      </Typography>
                    </Box>
                  );
                }

                // Affichage avec pastille de couleur pour les statuts sélectionnés
                const getStatusConfig = (status) => {
                  switch (status) {
                    case 'disponible':
                      return { color: 'success.main', text: 'Disponible' };
                    case 'réservé':
                      return { color: 'warning.main', text: 'Réservé' };
                    case 'vendu':
                      return { color: 'error.main', text: 'Vendu' };
                    default:
                      return { color: 'primary.main', text: status };
                  }
                };

                const config = getStatusConfig(selected);

                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      minHeight: 24,
                      width: 'calc(100% - 32px)', // Largeur réduite pour éviter la flèche
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: config.color,
                        flexShrink: 0, // Empêche la pastille de rétrécir
                      }}
                    />
                    <Typography variant="body1" fontWeight={600} noWrap>
                      {config.text}
                    </Typography>
                  </Box>
                );
              }}
            >
              <MenuItem value="">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    minHeight: 40,
                    width: '100%',
                  }}
                >
                  <AllInclusive color="primary" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1" fontWeight={600}>
                      Tous les statuts
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Disponible, réservé, vendu
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="disponible">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                    }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1" fontWeight={600}>
                      Disponible
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Prêt à la vente
                    </Typography>
                  </Box>
                  <Chip label={stats.disponibles} size="small" color="success" />
                </Box>
              </MenuItem>
              <MenuItem value="réservé">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: 'warning.main',
                    }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1" fontWeight={600}>
                      Réservé
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      En cours de transaction
                    </Typography>
                  </Box>
                  <Chip label={stats.reserves} size="small" color="warning" />
                </Box>
              </MenuItem>
              <MenuItem value="vendu">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: 'error.main',
                    }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1" fontWeight={600}>
                      Vendu
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Transaction terminée
                    </Typography>
                  </Box>
                  <Chip label={stats.vendus} size="small" color="error" />
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={1}>
          <IconButton
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            color="primary"
            title="Filtres avancés"
            sx={{
              border: isAdvancedOpen ? '2px solid' : '1px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
            }}
          >
            <FilterList />
            {isAdvancedOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Grid>
      </Grid>

      {/* Filtres avancés */}
      <Collapse in={isAdvancedOpen}>
        <Box
          sx={{
            mt: 3,
            p: 3,
            bgcolor: 'grey.50',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main',
              fontWeight: 700,
            }}
          >
            <FilterList />
            Filtres avancés
          </Typography>

          <Grid container spacing={4}>
            {/* Actions */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'flex-start',
                  height: '100%',
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={handleClearAll}
                  size="medium"
                  fullWidth
                  sx={{ borderRadius: 2 }}
                >
                  Effacer tous les filtres
                </Button>
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  {activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''} actif
                  {activeFiltersCount > 1 ? 's' : ''}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isPriceFilterEnabled}
                      onChange={(e) => handlePriceFilterToggle(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Filtrer par prix"
                />
                {isPriceFilterEnabled && (
                  <Box sx={{ width: '100%', mt: 2, display: 'flex', gap: 2 }}>
                    <TextField
                      label="Prix min (€)"
                      type="number"
                      value={priceMin}
                      onChange={handlePriceMinChange}
                      inputProps={{ min: 0, max: 1000000, step: 1000 }}
                      fullWidth
                    />
                    <TextField
                      label="Prix max (€)"
                      type="number"
                      value={priceMax}
                      onChange={handlePriceMaxChange}
                      inputProps={{ min: 0, max: 1000000, step: 1000 }}
                      fullWidth
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}
